import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, CreditCard, Repeat, Percent } from 'lucide-react';

function BusinessModelSection({ content }) {
  const talentTiers = [
    { tier: 'Free', price: '$0', features: ['Basic profile', 'Job search', 'Feed access'] },
    { tier: 'Professional', price: '$15/mo', features: ['Enhanced profile', 'Priority job alerts', 'Analytics'], popular: true },
    { tier: 'Premium', price: '$40/mo', features: ['Verified badge', 'Featured in search', 'Advanced analytics'] },
  ];

  const businessTiers = [
    { tier: 'Free', price: '$0', features: ['Basic listing', 'Limited job posts'] },
    { tier: 'Professional', price: '$50/mo', features: ['Unlimited jobs', 'Team management', 'Applicant tracking'], popular: true },
    { tier: 'Enterprise', price: '$200/mo', features: ['Multi-location', 'API access', 'Priority support'] },
  ];

  const transactions = [
    { type: 'Service Booking', fee: '3.75% + $2', icon: CreditCard, note: 'Business receives payment' },
    { type: 'Chair Rental', fee: '3.75% + $5', icon: Repeat, note: 'Recurring billing available' },
    { type: 'Venmo/CashApp', fee: '$2 flat', icon: DollarSign, note: 'For independent talent' },
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-gold/10 border border-accent-gold/20 rounded-full mb-6">
            <DollarSign className="w-4 h-4 text-accent-gold" />
            <span className="text-sm text-accent-gold">Business Model</span>
          </div>
          <h2 className="section-title mb-4">
            Multiple <span className="text-gradient">Revenue Streams</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Diversified revenue from subscriptions, transactions, and marketplace fees.
          </p>
        </motion.div>

        {/* Pricing Tables */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Talent Pricing */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center">Talent Subscriptions</h3>
            <div className="space-y-3">
              {talentTiers.map((tier) => (
                <div 
                  key={tier.tier}
                  className={`card p-4 ${tier.popular ? 'border-kore-500/50 bg-kore-500/5' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{tier.tier}</span>
                      {tier.popular && (
                        <span className="text-xs px-2 py-0.5 bg-kore-500 text-white rounded-full">Popular</span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-kore-400">{tier.price}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tier.features.map((f) => (
                      <span key={f} className="text-xs text-zinc-500">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Business Pricing */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center">Business Subscriptions</h3>
            <div className="space-y-3">
              {businessTiers.map((tier) => (
                <div 
                  key={tier.tier}
                  className={`card p-4 ${tier.popular ? 'border-accent-lavender/50 bg-accent-lavender/5' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{tier.tier}</span>
                      {tier.popular && (
                        <span className="text-xs px-2 py-0.5 bg-accent-lavender text-white rounded-full">Popular</span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-accent-lavender">{tier.price}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tier.features.map((f) => (
                      <span key={f} className="text-xs text-zinc-500">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Transaction Fees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <h3 className="text-lg font-semibold mb-6 text-center">Transaction Fees</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {transactions.map((tx, index) => {
              const Icon = tx.icon;
              return (
                <motion.div
                  key={tx.type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex p-3 rounded-xl bg-surface-elevated mb-3">
                    <Icon className="w-6 h-6 text-kore-400" />
                  </div>
                  <div className="font-medium mb-1">{tx.type}</div>
                  <div className="text-2xl font-bold text-gradient">{tx.fee}</div>
                  <div className="text-xs text-zinc-500 mt-1">{tx.note}</div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Additional Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-wrap justify-center gap-3 mt-8"
        >
          {['Job post boosts', 'Promoted profiles', 'Paid classifieds', 'Featured listings'].map((item) => (
            <span 
              key={item}
              className="px-4 py-2 bg-surface-card border border-surface-border rounded-full text-sm text-zinc-400"
            >
              {item}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default BusinessModelSection;
